import { useState, useEffect, useRef, useCallback } from 'react';
import { DashboardLayout } from '~/Layouts';
import {
    Container,
    PageBanner,
    PageHeader,
    Media,
    Breadcrumbs
} from '~/Common';
import { ContentBlock } from '~/ContentBlocks'
import {
    Box,
    Flex,
    Grid,
    Heading,
    Text
} from '@theme-ui/components'
import { connect, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group';
import moment from 'moment-timezone'
import { getCurrentCompanyAsync, getCurrentCompanyResearchReportAsync } from 'features/company/companySlice';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { ResearchRerportViewSkeleton } from './skeleton';
import { useResponsiveValue } from '@theme-ui/match-media';
import { DATE_FORMAT } from 'app/constants';
import { SidebarTable } from '~/Research';
import { formatHumanReadableDollarValue } from 'utils/helpers';
import { Icon } from 'assets/Icon';

const mapStateToProps = (state) => {
    return { user: state.user, companies: state.companies }
}

const ResearchReportView = ({ user, companies, match }) => {
    const { t } = useTranslation();
    useDocumentTitle(t(`Research - ${companies?.currentCompanyExtras?.currentResearchReport?.title || ''}`));
    const isMobile = useResponsiveValue([ true, true, true, false ]);
    const dispatch = useDispatch();
    const nodeRef = useRef(null);
    const [notFound, setNotFound] = useState(false);
    const [researchSlug, setResearchSlug] = useState('');

    const isLoading = companies.currentCompanyExtras?.currentResearchReportStatus === 'loading';
    const report = companies?.currentCompanyExtras?.currentResearchReport;
    const company = companies?.currentCompany;

    useEffect(() => {
        // Get the current report (if the slug is empty or has changed)
        if (companies.currentCompanyExtras?.currentResearchReportStatus === 'idle' || (match?.params?.researchSlug !== researchSlug)) {
            const newSlug = match?.params?.researchSlug;
            const companySlug = match?.params?.slug;
           
            setResearchSlug(newSlug);
            dispatch(getCurrentCompanyAsync(companySlug));
            dispatch(getCurrentCompanyResearchReportAsync({slug: companySlug, researchSlug: newSlug}));

            window.addEventListener('notFound', event => {
                setNotFound(true);
            });
        }
    }, [company, companies, researchSlug, match, dispatch]);

    moment.tz.setDefault(user?.user?.timezone);

    const removeInsignificantZeros = useCallback((value) => {
        // Remove any 0s after the last significant digit after the decimal point
        return parseFloat(value).toString();
    }
    , []);

    return (
        <DashboardLayout dark={true} hideCta={true} p={0}>
            <CSSTransition
                nodeRef={nodeRef}
                in={true}
                timeout={500}
                classNames="fade"
                unmountOnExit
                appear
            >
                <div ref={nodeRef}>
                    {isLoading ?
                        <ResearchRerportViewSkeleton />
                    : <>
                        <PageBanner img={'industries'}>
                            <PageHeader sx={{ position: 'relative' }}>
                                <Container variant="layouts.default">
                                        <Box sx={{ position: 'relative' }}>
                                            {notFound ? (
                                                <Box mb={4}>
                                                    <Heading as="h1" variant="display" mb={2}>{t('companies.notFound.title')}</Heading>
                                                    <Text as="p">{t('companies.notFound.description')}</Text>
                                                </Box>
                                            ) : (
                                                <>
                                                    {!isMobile && (
                                                        <Breadcrumbs
                                                            title={report?.title?.length > 20 ? report?.title?.substring(0, 20) + '...' : report?.title}
                                                            items={[
                                                                {
                                                                    title: 'Companies',
                                                                    to: '/companies'
                                                                },
                                                                {
                                                                    title: company?.name,
                                                                    to: `/companies/${company?.slug}`
                                                                },
                                                            ]} 
                                                        />
                                                    )}
                                                    <Box mb={4} sx={{ maxWidth: '900px' }}>
                                                        <Heading as="h2" variant="h1" sx={{ lineHeight: '110%', fontWeight: 400 }} mb={3}>{t(report?.title)}</Heading>
                                                        {report?.subtitle ? <Text as="p" variant="h4" mb={3} sx={{ fontSize: '18px' }}>{t(report?.subtitle)}</Text> : null}
                                                        <Text as="p" sx={{ fontSize: '16px', color: '#6A7175' }}>
                                                            {report?.published_at && moment(report?.published_at).format(DATE_FORMAT.date_format_friendly)}
                                                            {company?.category && ` | ${t(company?.category.name)}`} 
                                                            {report?.reading_time_human && ` | ${report?.reading_time_human} read time`}
                                                        </Text>
                                                    </Box>
                                                </>
                                            )}
                                        </Box>
                                        <Box sx={{
                                            maxHeight: '583px',
                                        }}>
                                            <Media
                                                sx={{ maxHeight: '583px' }}
                                                image={report?.banner?.original}
                                            />
                                        </Box>
                                </Container>
                            </PageHeader>
                        </PageBanner>
                        {!notFound && (
                            <Container sx={{ position: 'relative', zIndex: 1, paddingTop: isMobile ? 0 : undefined }} variant="layouts.default">
                                <Grid sx={{
                                    position: 'relative',
                                    gridGap: [1, 1, 1, 5],
                                    gridTemplateColumns: ['1fr', '1fr', '1fr', 'auto 300px', 'auto 412px'],
                                    width: '100%'
                                }}>
                                    <Box sx={{ order: [2, 2, 2, 1, 1] }}>
                                        {report?.content_blocks && report?.content_blocks.map((block, key) => (
                                            <ContentBlock
                                                type={block?.type}
                                                data={block?.data}
                                                container={block?.settings?.container}
                                                bg={'transparent'}
                                                blockKey={key + 1}
                                                live_at={block?.live_at}
                                                key={`block${key}`}
                                            />
                                        ))}
                                    </Box>
                                    <Flex sx={{ position: 'relative', flexDirection: 'column', gridGap: 2, pt: isMobile ? 0 : 32, order: [1, 1, 1, 2, 2] }}>
                                        {report?.current_share_price ? (
                                            <Box>
                                                <Heading as="h2" variant="content_block_heading" mb={isMobile ? 3 : 0}>{t('Current Share Price')}</Heading>
                                                <Flex sx={{ justifyContent: 'space-around' }}>
                                                    <Text as="p" sx={{ fontSize: isMobile ? '48px' : '78px', color: 'darker' }}>{`${removeInsignificantZeros(report?.current_share_price)}`}</Text>
                                                    <Flex>
                                                        <Icon icon={report?.price_change > 0 ? 'double-chevron-up' : parseFloat(report?.price_change) === 0 ? 'minus-circle' : 'double-chevron-down' } size={55} />
                                                        <Flex sx={{ flexDirection: 'column', justifyContent: 'center', ml: 2 }}>
                                                            <Text 
                                                                as="p"
                                                                variant='researchReportPriceChange'
                                                                color={report?.price_change_percentage > 0 ? 'tealLight' : parseFloat(report?.price_change_percentage) === 0 ? 'darker' : 'red'}
                                                            >
                                                                {report?.price_change_percentage}%
                                                            </Text>
                                                            <Text 
                                                                as="p"
                                                                variant='researchReportPriceChange'
                                                                color={report?.price_change > 0 ? 'tealLight' : parseFloat(report?.price_change) === 0 ? 'darker' : 'red'}
                                                            >
                                                                {parseFloat(report?.price_change) === 0 ? '-' : removeInsignificantZeros(report?.price_change)}
                                                            </Text>
                                                        </Flex>
                                                        
                                                    </Flex>
                                                </Flex>
                                                <Text as="p" variant='small' color='dark' ml={3}>{t('* change since report publish date')}</Text>
                                            </Box>
                                        ) : null}
                                        <Heading as="h2" variant="content_block_heading" mb={0} mt={3}>{t('Report Snapshot')}</Heading>
                                        <Text as="p" sx={{ fontSize: '16px', color: '#6A7175' }}>
                                            {report?.published_at && moment(report?.published_at).format(DATE_FORMAT.date_format_friendly)}
                                        </Text>
                                        <SidebarTable header='Company Statement' rows={[
                                            { name: 'GICS Industry Code', value: report?.gics_industry_code },
                                            { name: 'Location', value: report?.location },
                                            { name: 'Shares on Issue', value: report?.shares_on_issue },
                                            { name: 'Share Price', value: `$${removeInsignificantZeros(report?.share_price)}` },
                                            { name: '52 Week High / Low', value: `$${removeInsignificantZeros(report?.low_52w)}-$${removeInsignificantZeros(report?.high_52w)}` },
                                            { name: '90 Day Average Volume', value: report?.avg_volume_90d },
                                            { name: 'Market Capitalisation', value: `${formatHumanReadableDollarValue(report?.market_cap)}` },
                                        ]} />
                                        {Array.isArray(report?.management_lines) && report?.management_lines.length > 0 && (
                                            <SidebarTable header='Management' rows={report?.management_lines?.map((manager) => { return { name: manager.name, value: manager.position }})} />
                                        )}
                                        {Array.isArray(report?.major_shareholders) && report?.major_shareholders.length > 0 && (
                                            <SidebarTable header='Major Shareholders' rows={report?.major_shareholders?.map((holder) => {return { name: holder.name, value: `${holder.percentage}%` }})} />
                                        )}
                                        {report?.price_graph && report?.price_graph?.sizes?.sidebar && (
                                            <>
                                                <SidebarTable header='12 Month Performance' />
                                                <Media image={report?.price_graph?.sizes?.sidebar} objectFit={isMobile ? 'contain' : 'none'} radius='0px' />
                                            </>
                                        )}
                                    </Flex>
                                </Grid>
                            </Container>
                        )}
                    </>}
                </div>
            </CSSTransition>
        </DashboardLayout>
    );
};

export default connect(mapStateToProps, null)(ResearchReportView);
